import { withScriptFallback } from '@messenger/lotus/fonts'
import { regEx } from '@/utils/global/regEx'

/**
 * Sanitizes CSS style values for safe insertion into HTML attributes
 * Converts double quotes to single quotes to prevent breaking HTML attribute parsing
 */
export const sanitizeStyleValue = (value: string): string => {
  return value.replace(regEx.doubleQuote, "'")
}

/**
 * Converts font-size from px to pt
 * Standard conversion: 1pt = 4/3px (1.333px)
 * Therefore: pt = px * 0.75
 * Rounds to 2 decimal places for cleaner output
 */
const convertPxToPt = (fontSize: string): string => {
  if (!fontSize) return fontSize

  // Check if already in pt
  if (fontSize.endsWith('pt')) return fontSize

  // Extract numeric value from px
  const pxMatch = fontSize.match(/^([\d.]+)px$/i)
  if (!pxMatch) return fontSize

  const pxValue = parseFloat(pxMatch[1])
  if (isNaN(pxValue)) return fontSize

  // Convert px to pt: pt = px * 0.75
  const ptValue = (pxValue * 0.75).toFixed(2)

  // Remove trailing zeros and decimal point if not needed
  const cleanPtValue = ptValue.replace(/\.?0+$/, '')

  return `${cleanPtValue}pt`
}

/**
 * Helper function to get multiple computed styles from a node at once
 * Returns an object with style property names as keys and their values (or null)
 */
const getStyles = (
  editor: Editor,
  node: Node | Element,
  styleProperties: string[],
): Record<string, string | null> => {
  return styleProperties.reduce(
    (acc, property) => {
      acc[property] = editor.dom.getStyle(node, property, true) || null
      return acc
    },
    {} as Record<string, string | null>,
  )
}

/**
 * Gets inline formatting styles at current cursor position in TinyMCE
 * Returns computed styles (includes inline styles, CSS classes, and stylesheets)
 * Only returns styles if they appear to be user-customized
 * Captures: font family, font size, color, background-color, letter-spacing, text-transform, text-decoration, text-outline
 *
 * Note: Does NOT include block-level styles (line-height, text-align) to allow dynamic resizing
 * Note: Does NOT include bold/italic - these are handled via <strong>/<em> tags only
 */
export const getCursorPositionStyles = (editor: Editor): string => {
  if (!editor) {
    return ''
  }

  let currentNode = editor.selection.getNode()

  if (!currentNode) {
    return ''
  }

  // TinyMCE creates invisible "format-caret" containers to preserve formatting at cursor positions
  // (e.g., at the end of bold text, empty lines, or between differently formatted text).
  // The format-caret itself has no styles - the actual formatting lives in a styled <span> child.
  // We need to find this styled child to get the correct formatting, otherwise we'd get default styles.
  // Note: format-carets are TinyMCE internals and may not appear in the final HTML output.
  let formatCaretElement = null

  const isFormatCaretElement =
    currentNode.getAttribute?.('data-mce-type') === 'format-caret'

  if (isFormatCaretElement) {
    formatCaretElement = currentNode
  } else {
    formatCaretElement = currentNode.closest?.('[data-mce-type="format-caret"]')
  }

  if (formatCaretElement) {
    const styledChild = formatCaretElement.querySelector('span[style]')
    if (styledChild) {
      currentNode = styledChild
    }
  }

  // BANDAID FIX: If the current node is a block element (p, div, etc.), check if it contains
  // a styled span that was preserved by persistStylesOnBackspace. This happens when the cursor
  // is positioned in an empty paragraph that has a styled span wrapping a <br>.
  const isBlockElement = [
    'P',
    'DIV',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
  ].includes(currentNode.nodeName)

  if (isBlockElement) {
    const styledSpan = currentNode?.querySelector?.('span[style]')

    if (styledSpan) {
      currentNode = styledSpan
    }
  }

  const currentNodeStyles = getStyles(editor, currentNode, [
    'font-family',
    'font-size',
    'color',
    'background-color',
    'letter-spacing',
    'text-transform',
    'text-decoration',
    '-webkit-text-stroke',
  ])

  const {
    'font-family': fontFamily,
    'font-size': fontSize,
    color,
    'background-color': backgroundColor,
    'letter-spacing': letterSpacing,
    'text-transform': textTransform,
    'text-decoration': textDecoration,
  } = currentNodeStyles

  let textStroke = currentNodeStyles['-webkit-text-stroke']

  if (!textStroke) {
    const outlineElement = (currentNode as Element).classList?.contains(
      'outline',
    )
      ? currentNode
      : (currentNode as Element).closest('.outline')

    if (outlineElement) {
      textStroke =
        editor.dom.getStyle(outlineElement, '-webkit-text-stroke', true) || null
    }
  }

  const styles: string[] = []

  if (fontFamily) {
    // Re-append the Noto Script Fallback when re-writing cursor styles back
    // into inline form — without this, old saved documents whose spans only
    // have `font-family: 'Times New Roman'` would lose the fallback chain
    // every time TinyMCE recomputes the cursor position style. See #2517.
    styles.push(
      `font-family: ${withScriptFallback(sanitizeStyleValue(fontFamily))}`,
    )
  }

  if (fontSize) {
    // Always convert font-size to pt format for consistency
    const fontSizeInPt = convertPxToPt(fontSize)
    styles.push(`font-size: ${fontSizeInPt}`)
  }

  if (color) {
    styles.push(`color: ${color}`)
  }

  const hasVisibleBackgroundColor =
    backgroundColor &&
    !backgroundColor.includes('rgba(0, 0, 0, 0)') &&
    backgroundColor !== 'transparent'
  if (hasVisibleBackgroundColor) {
    styles.push(`background-color: ${backgroundColor}`)
  }

  const hasCustomLetterSpacing =
    letterSpacing && letterSpacing !== 'normal' && letterSpacing !== '0px'
  if (hasCustomLetterSpacing) {
    styles.push(`letter-spacing: ${letterSpacing}`)
  }

  const hasTextTransform = textTransform && textTransform !== 'none'
  if (hasTextTransform) {
    styles.push(`text-transform: ${textTransform}`)
  }

  const hasValidTextDecoration =
    textDecoration &&
    textDecoration !== 'none' &&
    !textDecoration.startsWith('rgb(') &&
    !textDecoration.startsWith('#')
  if (hasValidTextDecoration) {
    styles.push(`text-decoration: ${textDecoration}`)
  }

  const hasVisibleTextStroke =
    textStroke &&
    textStroke !== 'none' &&
    textStroke.includes('px') &&
    !textStroke.startsWith('0px')
  if (hasVisibleTextStroke) {
    styles.push(`-webkit-text-stroke: ${textStroke}`)
  }

  return styles.join('; ')
}

export default getCursorPositionStyles
